import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Search as SearchIcon,
  Psychology as PsychologyIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Feedback as FeedbackIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const ChatbotInfoPage = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" align="center" gutterBottom>
        Laprecio AI Assistant
      </Typography>
      
      <Typography variant="h6" align="center" color="textSecondary" paragraph>
        Our intelligent chatbot provides instant answers to your questions about the Laprecio platform
      </Typography>
      
      <Box sx={{ mt: 6, mb: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%' }}>
              <Typography variant="h4" gutterBottom>
                How It Works
              </Typography>
              
              <Typography paragraph>
                The Laprecio AI Assistant uses advanced Retrieval-Augmented Generation (RAG) technology to provide accurate, 
                context-aware responses to your questions about our platform, trading processes, compliance requirements, 
                and more.
              </Typography>
              
              <Typography paragraph>
                Unlike traditional chatbots that rely on pre-programmed responses, our AI Assistant can understand the 
                context of your questions and provide relevant, detailed information tailored to your specific needs.
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <SearchIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Intelligent Retrieval" 
                    secondary="Finds the most relevant information from our knowledge base" 
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <PsychologyIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Natural Language Understanding" 
                    secondary="Comprehends your questions even when phrased in different ways" 
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <SpeedIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Instant Responses" 
                    secondary="Provides immediate answers without waiting for human support" 
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <FeedbackIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Continuous Improvement" 
                    secondary="Gets smarter over time based on user interactions and feedback" 
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardMedia
                component="img"
                height="300"
                image="/images/chatbot-illustration.jpg"
                alt="AI Assistant Illustration"
                sx={{ objectFit: 'cover' }}
              />
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Try It Now
                </Typography>
                
                <Typography paragraph>
                  Our AI Assistant is available 24/7 to answer your questions about:
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <ChatIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Platform features and capabilities" />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <ChatIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Registration and account setup" />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <ChatIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Trading processes and fees" />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <ChatIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Compliance and regulatory requirements" />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <ChatIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Security measures and data protection" />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <ChatIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Technical support and troubleshooting" />
                  </ListItem>
                </List>
                
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="large"
                    startIcon={<ChatIcon />}
                    component={Link}
                    to="/"
                    sx={{ px: 4 }}
                  >
                    Chat with AI Assistant
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      <Divider sx={{ my: 6 }} />
      
      <Box sx={{ mt: 6 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Privacy & Security
        </Typography>
        
        <Grid container spacing={4} justifyContent="center" sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
              <SecurityIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Data Protection
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Your conversations with our AI Assistant are encrypted and protected according to our privacy policy.
                We do not store personally identifiable information from your chats.
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', textAlign: 'center' }}>
              <PsychologyIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Continuous Learning
              </Typography>
              <Typography variant="body2" color="textSecondary">
                We use anonymized conversation data to improve our AI Assistant's responses and knowledge base,
                ensuring it provides more accurate and helpful information over time.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default ChatbotInfoPage;
